import React, { ChangeEventHandler, FC, useMemo, useState } from 'react'
import * as LocalStyles from './styles'
import * as Styles from '../styles'
import { useKeyOnChange, useModalToggle } from '../hooks'
import ModalTemplate from 'uiKit/ModalTemplate'
import { SparklesIcon } from 'uiKit/icons/SparklesIcon'
import { useUnknownQuestions } from '../context'
import Select from 'react-select'
import SelectorStyles from '../../../constants/selectorStyles'
import styles from './styles.module.scss'
import { AssignAutocomplete } from '../assignUser/autocomplete/AssignAutocomplete'
import { UserAutocomplete } from '../assignUser/model/internal-types'
import { Checkbox, FormControlLabel } from '@material-ui/core'
import { TrainButtonState } from '../util/button'
import { Option } from '../../../../../helpers/selects'
import { unansweredQuestionTypeLabels } from '../configs'

type UnknownQuestionCreateModalProps = {
  toggleModal: () => void
  isOpen: boolean
  questionTypes: string[]
}

const Component: FC<UnknownQuestionCreateModalProps> = ({ toggleModal, isOpen, questionTypes }) => {
  const { createQuestion } = useUnknownQuestions()

  const [autocompleteValue, setAutocompleteValue] = useState<UserAutocomplete | null>(null)
  const [sendNotificationSend, setSendNotificationSend] = useState(false)
  const [type, setType] = useState<string>()
  const [question, setQuestion] = useState<string>()
  const [answer, setAnswer] = useState<string>()

  const isDataValid = useMemo(() => !!question && !!answer && !!type, [question, answer, type])

  const onUpdateButtonPressed = async (train = false) => {
    if (!isDataValid) return

    const params = {
      question,
      answer,
      type,
      train,
    }

    await createQuestion(params, autocompleteValue, sendNotificationSend, true)
    toggleModal()
  }

  const onAnswerChange: ChangeEventHandler<HTMLTextAreaElement> = event => {
    setAnswer(event.target.value)
  }

  const onQuestionChange: ChangeEventHandler<HTMLTextAreaElement> = event => {
    setQuestion(event.target.value)
  }

  const onSelectValueChange = event => {
    setType(event?.value)
    sendNotificationSend
  }

  const questionTypeLabels: Option[] = questionTypes
    .filter(value => value !== 'unknown')
    .map(value => {
      const label = unansweredQuestionTypeLabels[value] || value
      return { value, label }
    })

  return (
    <>
      <LocalStyles.CreateQuestion onClick={toggleModal} state={TrainButtonState.ACTIVE}>
        Add New Question
      </LocalStyles.CreateQuestion>

      <ModalTemplate
        containerClasses={styles.modal}
        open={isOpen}
        title="Create custom question"
        titleIcon={
          <Styles.TitleIconWrapper>
            <SparklesIcon />
          </Styles.TitleIconWrapper>
        }
        onClose={toggleModal}
        withFooter={false}>
        <Styles.ModalContainer className={styles.modalBox}>
          <Styles.SubHeader>Add possible question that user may ask.</Styles.SubHeader>

          <Styles.AnswerInput placeholder="Enter question here..." value={question} onChange={onQuestionChange} />

          <Select
            styles={SelectorStyles}
            options={questionTypeLabels}
            onChange={onSelectValueChange}
            placeholder="Select question type"
          />

          <Styles.SubHeader>Add desired bot response to this specific question</Styles.SubHeader>

          <Styles.AnswerInput placeholder="Enter answer here..." value={answer} onChange={onAnswerChange} />

          <Styles.SubHeader>Assign admin to question</Styles.SubHeader>

          <LocalStyles.AssignAdminBlock>
            <AssignAutocomplete setAutocompleteValue={setAutocompleteValue} autocompleteValue={autocompleteValue} />
            <FormControlLabel
              disabled={!autocompleteValue}
              control={
                <Checkbox
                  color="primary"
                  checked={sendNotificationSend}
                  onChange={(_e, checked) => setSendNotificationSend(checked)}
                />
              }
              style={{ color: 'green' }}
              label="Send Notification"
            />
          </LocalStyles.AssignAdminBlock>

          <Styles.ModalButtonContainer>
            <LocalStyles.CancelButton onClick={toggleModal}>Cancel</LocalStyles.CancelButton>
            <LocalStyles.ModalButton
              onClick={() => onUpdateButtonPressed()}
              state={isDataValid ? TrainButtonState.ACTIVE : TrainButtonState.INACTIVE}>
              Add
            </LocalStyles.ModalButton>
            <LocalStyles.ModalButton
              onClick={() => onUpdateButtonPressed(true)}
              state={isDataValid ? TrainButtonState.ACTIVE : TrainButtonState.INACTIVE}>
              Add and Train
            </LocalStyles.ModalButton>
          </Styles.ModalButtonContainer>
        </Styles.ModalContainer>
      </ModalTemplate>
    </>
  )
}

export const UnknownQuestionCreateModal: FC<{ questionTypes: string[] }> = ({ questionTypes }) => {
  const { isOpen, toggleModal } = useModalToggle()

  const key = useKeyOnChange(isOpen)

  return <Component {...{ isOpen, toggleModal, key, questionTypes }} />
}
